import { BACKEND_URL } from '../config/app.config';

// Helper to check if we're in development mode
const isDevelopment = import.meta.env.MODE === 'development';

// Base URL for upload routes (same worker, different path prefix)
const UPLOAD_URL = BACKEND_URL.replace('/qdrant', '');

export interface UploadFile {
    file: string;
    token: string;
    audio_data: string;
    sample_rate: number;
    duration: number;
    is_fill: boolean;
}

export interface UploadAlbumRequest {
    album_name: string;
    genre: string;
    bpm: number;
    /** Time signature numerator: 4 = 4/4, 3 = 3/4 or 6/8. Defaults to 4. */
    pulse_compas: number;
    tos_accepted: true;
    files: UploadFile[];
}

export interface UploadedAlbum {
    album_id: string;
    album_name: string;
    genre: string;
    bpm: number;
    file_count: number;
    status: string;
    uploaded_at: number;
}

/**
 * A user's approved uploaded album as it appears in the production library.
 * `librosa_album` is the factory-generated album name in librosa_min/librosa_audio
 * (e.g. "blues_70BPM_5f073160") — the identifier used by the generator.
 */
export interface MyLibraryAlbum {
    album_id: string;
    album_name: string;
    genre: string;
    bpm: number;
    /** Time signature numerator: 4 = 4/4, 3 = 3/4 or 6/8. Defaults to 4. */
    pulse_compas: number;
    librosa_album: string;
}

/**
 * Convert a browser File object to a base64-encoded string.
 */
export async function fileToBase64(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary);
}

export class UploadService {
    /**
     * Perform a credentialed fetch to the upload worker and return parsed JSON.
     * Throws on non-OK HTTP responses.
     */
    private async _request<T>(
        method: 'GET' | 'POST' | 'DELETE',
        path: string,
        body?: object,
    ): Promise<T> {
        const url = `${UPLOAD_URL}${path}`;
        const response = await fetch(url, {
            method,
            headers: body ? { 'Content-Type': 'application/json' } : undefined,
            credentials: 'include',
            body: body ? JSON.stringify(body) : undefined,
        });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json() as Promise<T>;
    }

    /**
     * Submit an album to the staging upload pipeline.
     * POST /uploads/album
     */
    async uploadAlbum(req: UploadAlbumRequest): Promise<{ ok: boolean; album_id: string }> {
        try {
            if (isDevelopment) {
                console.log('[Upload] Submitting album:', req.album_name, 'files:', req.files.length);
            }

            const data = await this._request<{ ok: boolean; album_id: string }>(
                'POST', '/uploads/album', req,
            );

            if (isDevelopment) {
                console.log('[Upload] Album submitted, album_id:', data.album_id);
            }

            return data;
        } catch (error) {
            if (isDevelopment) { console.error('[Upload] Error submitting album:', error); }
            throw new Error(`Failed to upload album: ${(error as Error).message}`);
        }
    }

    /**
     * List all staged uploads for the authenticated user.
     * GET /uploads/list
     */
    async listUploads(): Promise<UploadedAlbum[]> {
        try {
            if (isDevelopment) {
                console.log('[Upload] Fetching upload list');
            }

            const data = await this._request<{ uploads: UploadedAlbum[] }>('GET', '/uploads/list');

            if (isDevelopment) {
                console.log('[Upload] Found uploads:', data.uploads.length);
            }

            return data.uploads;
        } catch (error) {
            if (isDevelopment) { console.error('[Upload] Error fetching uploads:', error); }
            throw new Error(`Failed to list uploads: ${(error as Error).message}`);
        }
    }

    /**
     * Get the user's approved uploaded albums resolved to their librosa_min album names.
     * GET /uploads/my-library-albums
     *
     * Returns only albums that have been promoted by the factory and are present in
     * the production library. Albums still staged or rejected are excluded.
     */
    async getMyLibraryAlbums(): Promise<MyLibraryAlbum[]> {
        try {
            if (isDevelopment) {
                console.log('[Upload] Fetching my library albums');
            }

            const data = await this._request<{ albums: MyLibraryAlbum[] }>(
                'GET', '/uploads/my-library-albums',
            );

            if (isDevelopment) {
                console.log('[Upload] Found library albums:', data.albums.length);
            }

            return data.albums;
        } catch (error) {
            if (isDevelopment) { console.error('[Upload] Error fetching library albums:', error); }
            throw new Error(`Failed to fetch library albums: ${(error as Error).message}`);
        }
    }

    /**
     * Delete a staged upload by album ID.
     * DELETE /uploads/album/:album_id
     */
    async deleteUpload(albumId: string): Promise<{ ok: boolean }> {
        try {
            if (isDevelopment) {
                console.log('[Upload] Deleting upload:', albumId);
            }

            const data = await this._request<{ ok: boolean }>(
                'DELETE', `/uploads/album/${albumId}`,
            );

            if (isDevelopment) {
                console.log('[Upload] Deleted album_id:', albumId);
            }

            return data;
        } catch (error) {
            if (isDevelopment) { console.error('[Upload] Error deleting upload:', error); }
            throw new Error(`Failed to delete upload: ${(error as Error).message}`);
        }
    }
}
